import { Editor, Transforms, Element as SlateElement, Text } from 'slate';

export const toggleMultipleRowsFormat = (editor, format) => {
    if (!editor.selectedRows?.length) return;

    const isActive = isFormatActiveInMultipleSelectedRows(editor, format);

    if (isActive) {
        removeFormatFromMultipleSelectedRows(editor, format);
    } else {
        applyFormatToMultipleSelectedRows(editor, format, true);
    }
};

export const isMultipleRowsAlignmentActive = (editor, format) => {
    if (!editor.selectedRows?.length) return false;

    return editor.selectedRows.every(({ tablePath, rowIndex }) => {
        try {
            const [tableNode] = Editor.node(editor, tablePath);
            if (!SlateElement.isElement(tableNode)) return false;

            const row = tableNode.children[rowIndex];
            if (SlateElement.isElement(row)) {
                return row.children.every((cell, cellIndex) => {
                    const cellPath = [...tablePath, rowIndex, cellIndex];
                    const [cellNode] = Editor.node(editor, cellPath);

                    return SlateElement.isElement(cellNode) && cellNode.textAlign === format;
                });
            }
            return false;
        } catch (error) {
            console.error('Error checking alignment in row:', error);
            return false;
        }
    });
};

export const isFormatActiveInMultipleSelectedRows = (editor, format) => {
    if (!editor.selectedRows?.length) return false;

    return editor.selectedRows.every(({ tablePath, rowIndex }) => {
        try {
            const [tableNode] = Editor.node(editor, tablePath);
            if (!SlateElement.isElement(tableNode)) return false;

            const row = tableNode.children[rowIndex];
            if (SlateElement.isElement(row)) {
                return row.children.every((cell, cellIndex) => {
                    const cellPath = [...tablePath, rowIndex, cellIndex];

                    const textNodes = Array.from(
                        Editor.nodes(editor, {
                            at: cellPath,
                            match: (n) => Text.isText(n),
                        }),
                    );

                    return textNodes.length > 0 && textNodes.every(([textNode]) => textNode[format] === true);
                });
            }
            return false;
        } catch (error) {
            console.error('Error checking format in row:', error);
            return false;
        }
    });
};

export const applyFormatToMultipleSelectedRows = (editor, format, value = true) => {
    if (!editor.selectedRows?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedRows.forEach(({ tablePath, rowIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                const row = tableNode.children[rowIndex];
                if (SlateElement.isElement(row)) {
                    row.children.forEach((cell, cellIndex) => {
                        const cellPath = [...tablePath, rowIndex, cellIndex];
                        const textNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => Text.isText(n),
                            }),
                        );
                        textNodes.forEach(([textNode, textPath]) => {
                            Transforms.setNodes(editor, { [format]: value }, { at: textPath });
                        });
                    });
                }
            } catch (error) {
                console.error('Error applying format to row:', error);
            }
        });

        editor.onChange();
    });
};

export const removeFormatFromMultipleSelectedRows = (editor, format) => {
    if (!editor.selectedRows?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedRows.forEach(({ tablePath, rowIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                const row = tableNode.children[rowIndex];
                if (SlateElement.isElement(row)) {
                    row.children.forEach((cell, cellIndex) => {
                        const cellPath = [...tablePath, rowIndex, cellIndex];

                        const textNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => Text.isText(n),
                            }),
                        );

                        textNodes.forEach(([textNode, textPath]) => {
                            Transforms.unsetNodes(editor, [format], { at: textPath });
                        });
                    });
                }
            } catch (error) {
                console.error('Error removing format from row:', error);
            }
        });

        editor.onChange();
    });
};

export const applyFormatToMultipleSelectedColumns = (editor, format, value = true) => {
    if (!editor.selectedColumns?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedColumns.forEach(({ tablePath, columnIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                tableNode.children.forEach((row, rowIndex) => {
                    if (SlateElement.isElement(row) && columnIndex < row.children.length) {
                        const cellPath = [...tablePath, rowIndex, columnIndex];
                        const textNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => Text.isText(n),
                            }),
                        );

                        textNodes.forEach(([textNode, textPath]) => {
                            Transforms.setNodes(editor, { [format]: value }, { at: textPath });
                        });
                    }
                });
            } catch (error) {
                console.error('Error applying format to column:', error);
            }
        });
        editor.onChange();
    });
};

export const removeFormatFromMultipleSelectedColumns = (editor, format) => {
    if (!editor.selectedColumns?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedColumns.forEach(({ tablePath, columnIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                tableNode.children.forEach((row, rowIndex) => {
                    if (SlateElement.isElement(row) && columnIndex < row.children.length) {
                        const cellPath = [...tablePath, rowIndex, columnIndex];

                        const textNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => Text.isText(n),
                            }),
                        );

                        textNodes.forEach(([textNode, textPath]) => {
                            Transforms.unsetNodes(editor, [format], { at: textPath });
                        });
                    }
                });
            } catch (error) {
                console.error('Error removing format from column:', error);
            }
        });

        editor.onChange();
    });
};

export const isFormatActiveInMultipleSelectedColumns = (editor, format) => {
    if (!editor.selectedColumns?.length) return false;

    return editor.selectedColumns.every(({ tablePath, columnIndex }) => {
        try {
            const [tableNode] = Editor.node(editor, tablePath);
            if (!SlateElement.isElement(tableNode)) return false;

            return tableNode.children.every((row, rowIndex) => {
                if (SlateElement.isElement(row) && columnIndex < row.children.length) {
                    const cellPath = [...tablePath, rowIndex, columnIndex];

                    const textNodes = Array.from(
                        Editor.nodes(editor, {
                            at: cellPath,
                            match: (n) => Text.isText(n),
                        }),
                    );
                    return textNodes.length > 0 && textNodes.every(([textNode]) => textNode[format] === true);
                }
                return true; // Skip cells that don't exist in this row
            });
        } catch (error) {
            console.error('Error checking format in column:', error);
            return false;
        }
    });
};

export const toggleMultipleColumnsFormat = (editor, format) => {
    if (!editor.selectedColumns?.length) return;

    const isActive = isFormatActiveInMultipleSelectedColumns(editor, format);

    if (isActive) {
        removeFormatFromMultipleSelectedColumns(editor, format);
    } else {
        applyFormatToMultipleSelectedColumns(editor, format, true);
    }
};

export const isMultipleColumnsAlignmentActive = (editor, format) => {
    if (!editor.selectedColumns?.length) return false;

    return editor.selectedColumns.every(({ tablePath, columnIndex }) => {
        try {
            const [tableNode] = Editor.node(editor, tablePath);
            if (!SlateElement.isElement(tableNode)) return false;

            // let hasAlignment = false;
            return tableNode.children.every((row, rowIndex) => {
                if (SlateElement.isElement(row) && columnIndex < row.children.length) {
                    const cellPath = [...tablePath, rowIndex, columnIndex];
                    const [cellNode] = Editor.node(editor, cellPath);

                    return SlateElement.isElement(cellNode) && cellNode.textAlign === format;
                }
                return true; // Skip rows where the column doesn't exist
            });
        } catch (error) {
            console.error('Error checking alignment in column:', error);
            return false;
        }
    });
};
export const toggleMultipleColumnsAlignment = (editor, format) => {
    if (!editor.selectedColumns?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedColumns.forEach(({ tablePath, columnIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                tableNode.children.forEach((row, rowIndex) => {
                    if (SlateElement.isElement(row) && columnIndex < row.children.length) {
                        const cellPath = [...tablePath, rowIndex, columnIndex];
                        const blockNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => {
                                    return SlateElement.isElement(n) && Editor.isBlock(editor, n) && n.type === 'paragraph';
                                },
                            }),
                        );
                        blockNodes.forEach(([blockNode, blockPath]) => {
                            Transforms.setNodes(editor, { textAlign: format }, { at: blockPath });
                        });
                    }
                });
            } catch (error) {
                console.error('Error applying alignment to column:', error);
            }
        });

        editor.onChange();
    });
};

export const toggleMultipleRowsAlignment = (editor, format) => {
    if (!editor.selectedRows?.length) return;

    Editor.withoutNormalizing(editor, () => {
        editor.selectedRows.forEach(({ tablePath, rowIndex }) => {
            try {
                const [tableNode] = Editor.node(editor, tablePath);

                if (!SlateElement.isElement(tableNode)) return;

                const row = tableNode.children[rowIndex];
                if (SlateElement.isElement(row)) {
                    row.children.forEach((cell, cellIndex) => {
                        const cellPath = [...tablePath, rowIndex, cellIndex];

                        // ✅ Apply alignment to block elements inside the cell
                        const blockNodes = Array.from(
                            Editor.nodes(editor, {
                                at: cellPath,
                                match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
                            }),
                        );

                        blockNodes.forEach(([blockNode, blockPath]) => {
                            Transforms.setNodes(editor, { textAlign: format }, { at: blockPath });
                        });
                    });
                }
            } catch (error) {
                console.error('Error applying alignment to row:', error);
            }
        });

        editor.onChange();
    });
};
